import { useSelector } from 'react-redux'

import * as yup from 'yup'
import { Form, Formik } from 'formik'

import { Box, Button, Grid, MenuItem, Select } from '@material-ui/core'

import { ConnectedInput, ConnectedSelect } from 'UI/Components'

import { useAuth, useRoles, useNotification, useOrgFeatureFlags } from 'hooks'

import { logToAnalytics, languageOptions, uiId, updateOrganization } from 'modules'

type ValuesType = {
    scheduleURL: string | null
    requestRouting: string | null
    defaultLanguage: string
}

export const PreferencesTab = () => {
    const { orgId } = useAuth()
    const { isAdmin } = useRoles()
    const { showSuccessNotification } = useNotification()
    const { isAskGuiddeShown } = useOrgFeatureFlags()

    const { scheduleURL, requestRouting, defaultLanguage } =
        useSelector(state => state.firestore.data?.organizations?.[orgId]) || {}

    const onSubmit = (values: ValuesType) => {
        updateOrganization({ id: orgId, ...values })
            .catch(console.error)
            .then(() => {
                showSuccessNotification('Organization has been updated')
                logToAnalytics('change_settings')
            })
    }

    return (
        <Formik
            enableReinitialize
            onSubmit={onSubmit}
            initialValues={{
                scheduleURL: scheduleURL || null,
                requestRouting: requestRouting || null,
                defaultLanguage: defaultLanguage || ''
            }}
            validationSchema={
                isAskGuiddeShown &&
                yup.object().shape({
                    scheduleURL: yup
                        .string()
                        .trim()
                        .nullable()
                        .url('Enter valid link: https://example.com'),
                    defaultLanguage: yup.string().trim().nullable(),
                    requestRouting: yup
                        .array()
                        .nullable()
                        .transform((value, originalValue) => {
                            if (value !== null) return value

                            return originalValue ? originalValue.split(/[,]+/) : []
                        })
                        .of(
                            yup
                                .string()
                                .nullable()
                                .required('Email after comma is required')
                                .email(({ value }) =>
                                    /\s/.test(value)
                                        ? 'Whitespace is not allowed, use comma to separate emails'
                                        : `${value} is not a valid email `
                                )
                        )
                })
            }
        >
            <Box mt={2}>
                <Form>
                    {isAdmin && isAskGuiddeShown && (
                        <Row
                            label="Schedule url"
                            item={
                                <ConnectedInput
                                    fullWidth
                                    name="scheduleURL"
                                    placeholder="Enter calendar link"
                                    id={uiId('edit-org-calendar-link-input')}
                                />
                            }
                        />
                    )}

                    {isAdmin && isAskGuiddeShown && (
                        <Row
                            label="Request Routing"
                            item={
                                <ConnectedInput
                                    fullWidth
                                    id={uiId('edit-org-request-routing-input')}
                                    name="requestRouting"
                                    placeholder="Enter emails separated by comma"
                                />
                            }
                        />
                    )}

                    {/*PRIMARY LANGUAGE PLACEHOLDER*/}
                    <Row
                        label="Primary lang"
                        item={
                            <Select
                                disabled
                                value="en-US"
                                style={{ width: '300px' }}
                                id={uiId('edit-org-primary-lang-select')}
                            >
                                <MenuItem value="en-US">English</MenuItem>
                            </Select>
                        }
                    />

                    <Row
                        label="Secondary lang"
                        item={
                            <ConnectedSelect
                                id={uiId('edit-org-secondary-lang-select')}
                                name="defaultLanguage"
                                options={languageOptions}
                            />
                        }
                    />

                    <Box mt={2}>
                        <Button size="medium" type="submit" id={uiId('edit-org-update-btn')}>
                            Update
                        </Button>
                    </Box>
                </Form>
            </Box>
        </Formik>
    )
}

type RowProps = {
    label: string
    item: JSX.Element
}

const Row = ({ label, item }: RowProps) => {
    return (
        <Box mb={2}>
            <Grid container alignItems="center">
                <Grid item xs={2}>
                    <b>{label}</b>
                </Grid>
                <Grid item xs={10}>
                    {item}
                </Grid>
            </Grid>
        </Box>
    )
}
